import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import { Check, Loader2, Plus, Search } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { Card, CardContent, Skeleton, TextField } from "@fleet/ui";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import type { components } from "@time-tracker/shared";
import { FontDownloaderService } from "@/utils/font-downloader";
import { showToast } from "@/components/ui/toast";
import { useI18n } from "@/providers/i18n-provider";
import { useAdminCapabilities } from "../use-admin-capabilities";

type ErrorModel = components["schemas"]["ErrorModel"];
type FontRegistryResponse = components["schemas"]["FontRegistryResponse"];
type RegistryFont = components["schemas"]["RegistryFontDTO"];

const PAGE_SIZE = 50;

/**
 * "Available fonts" tab: browse the third-party Fontsource registry (~1700
 * families) and approve any into the local catalog. Approving ingests the font
 * with stable CDN (jsDelivr) woff2 URLs — no self-hosting — and activates it.
 * The font preview is loaded local-first (Cache Storage) via FontDownloader.
 */
export function FontRegistryPanel() {
  const { t } = useI18n();
  const { can } = useAdminCapabilities();

  const [search, setSearch] = createSignal("");
  const [page, setPage] = createSignal(1);
  const [previewing, setPreviewing] = createSignal<string | null>(null);

  const query = createApiQuery<FontRegistryResponse, ErrorModel>(
    () => ["admin", "font-registry", { search: search(), page: page() }],
    async () =>
      api.GET("/api/admin/font/registry", {
        params: {
          query: {
            search: search().trim() || null,
            page: page(),
            pageSize: PAGE_SIZE,
          },
        },
      }),
    () => ({ staleTime: 60_000 }),
  );

  const approveMutation = createApiMutation(async (vars: { id: string }) =>
    api.POST("/api/admin/font/approve", { body: vars }),
  );

  const totalPages = createMemo(() => {
    const total = query.data?.total ?? 0;
    return Math.max(1, Math.ceil(total / PAGE_SIZE));
  });

  const handleApprove = (font: RegistryFont) => {
    const { family } = font;
    approveMutation.mutate(
      { id: font.id },
      {
        onSuccess: () => {
          void query.refetch();
          showToast(t(msg`Font "${family}" approved`), "success");
        },
        onError: () => {
          showToast(t(msg`Failed to approve "${family}"`), "error");
        },
      },
    );
  };

  // Loads the CDN preview for a single family (weight 400) on demand, local-first.
  const handlePreview = (font: RegistryFont) => {
    setPreviewing(font.family);
    const url = `https://cdn.jsdelivr.net/npm/@fontsource/${font.id}@5/files/${font.id}-latin-400-normal.woff2`;
    void FontDownloaderService.downloadAndLoad(font.family, [{ weight: "400", url }]).finally(
      () => {
        setPreviewing(null);
      },
    );
  };

  const onSearchInput = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  return (
    <div class="space-y-6">
      <div class="relative">
        <Search class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <TextField
          placeholder={t(msg`Search Fontsource (e.g., Inter, Roboto...)`)}
          class="pl-9"
          value={search()}
          onInput={(e) => {
            onSearchInput(e.currentTarget.value);
          }}
        />
      </div>

      {/* P23 / CC-60 - I3: <<no se pudo cargar>> NO es <<no hay nada>>. Con el API
          caido este panel pintaba su estado VACIO como si el dato fuera real. El
          bloque de contenido se ENVUELVE en lugar de encadenar `&& !query.isError`
          en el guard existente: en error `isLoading` es `false`, asi que el guard
          pintaria su esqueleto Y la caja de error a la vez, para siempre. */}
      <Show when={query.isError}>
        <QueryErrorState
          testId="admin-font-registry-error"
          compact
          onRetry={() => void query.refetch()}
        />
      </Show>
      <Show when={!query.isError}>
        <Show
          when={!query.isLoading}
          fallback={
            <div class="grid gap-3 md:grid-cols-2">
              <For each={Array.from({ length: 6 }, () => 0)}>
                {() => <Skeleton class="h-20 w-full rounded-xl" />}
              </For>
            </div>
          }
        >
          <div class="grid gap-3 md:grid-cols-2">
            <For each={query.data?.items ?? []}>
              {(font) => (
                <Card>
                  <CardContent class="p-4 flex items-center justify-between gap-3">
                    <button
                      type="button"
                      class="min-w-0 flex-1 text-left cursor-pointer"
                      onClick={() => {
                        handlePreview(font);
                      }}
                      title={t(msg`Click to preview`)}
                    >
                      <div
                        class="truncate text-lg"
                        style={{ "font-family": `"${font.family}", sans-serif` }}
                      >
                        {font.family}
                      </div>
                      <div class="text-xs text-muted-foreground">
                        {font.category}
                        <Show when={previewing() === font.family}>
                          {" · "}
                          {t(msg`loading preview…`)}
                        </Show>
                      </div>
                    </button>

                    <Show
                      when={!font.approved}
                      fallback={
                        <span class="flex items-center gap-1 text-sm text-accent shrink-0">
                          <Check class="h-4 w-4" /> {t(msg`Approved`)}
                        </span>
                      }
                    >
                      <Button
                        size="sm"
                        variant="outline"
                        class="shrink-0"
                        disabled={approveMutation.isPending || !can("content.write")}
                        onClick={() => {
                          handleApprove(font);
                        }}
                      >
                        <Plus class="mr-1 h-4 w-4" /> {t(msg`Approve`)}
                      </Button>
                    </Show>
                  </CardContent>
                </Card>
              )}
            </For>
          </div>

          <div class="flex items-center justify-between pt-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={page() <= 1}
              onClick={() => {
                setPage((p) => Math.max(1, p - 1));
              }}
            >
              {t(msg`Previous`)}
            </Button>
            <span class="text-sm text-muted-foreground">
              {/* oxlint-disable-next-line lingui/no-expression-in-message */}
              {t(msg`Page ${page()} of ${totalPages()}`)}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={page() >= totalPages()}
              onClick={() => {
                setPage((p) => p + 1);
              }}
            >
              <Show when={approveMutation.isPending} fallback={t(msg`Next`)}>
                <Loader2 class="h-4 w-4 animate-spin" />
              </Show>
            </Button>
          </div>
        </Show>
      </Show>
    </div>
  );
}
